/**
 * imageUploadService — common image/video upload via the image handler (exsportscoreapi).
 *
 * All uploads use the presigned S3 URL flow (file goes browser→S3 directly):
 *   POST /upload/init → PUT directly to S3 → POST /upload/complete (Rekognition)
 *
 * This bypasses CloudFront WAF which blocks POST bodies > 8KB via SizeRestrictions_BODY.
 *
 * Env: REACT_APP_IMAGE_HANDLER_CONTEXT_PATH — context path for the image handler (e.g. /exsportscoreapi)
 *
 * Usage:
 *   import { uploadFile } from '../../services/imageUploadService'
 *   const { key, url } = await uploadFile(file, 'group_assets')
 */

import { apiImageCall } from './apiUtil'
import { isCapacitor } from './googleAuthService'

// Per-type client-side size limits
export const UPLOAD_SIZE_LIMITS = {
  image: 10 * 1024 * 1024,  // 10MB
  video: 20 * 1024 * 1024,  // 20MB
}

const VIDEO_MIME_TYPES = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm']

export class FileTooLargeError extends Error {
  constructor(sizeMB, limitMB) {
    super(`This file is ${sizeMB} MB. Please choose a file under ${limitMB} MB.`)
    this.name = 'FileTooLargeError'
  }
}

/**
 * Call this in any upload error handler instead of writing the message inline.
 * Returns true if the error was a content policy violation (so caller can reset UI),
 * false otherwise.
 *
 * Triggers the global ContentPolicyModal (mounted in App.jsx) via a DOM event.
 */
export function handleUploadError(error) {
  if (error instanceof ContentPolicyError) {
    window.dispatchEvent(new CustomEvent('content-policy-violation'))
    return true
  }
  return false
}

export function uploadErrorMessage(error) {
  if (error instanceof FileTooLargeError) return error.message
  if (error?.status === 413) return 'This file is too large to upload. Please choose a photo under 10 MB.'
  return error?.message || 'Upload failed. Please try again.'
}

/**
 * Upload a file with inline moderation.
 *
 * @param {File|Blob} file      - The file to upload
 * @param {string}    fileType  - One of: profile_images | posts_assets | group_assets |
 *                                team_assets | tournament_assets
 * @param {string}    [fileName] - Override filename (useful when passing a Blob)
 * @param {string}    [entityPath] - S3 key suffix (e.g. "postId/timestamp_0.jpg")
 * @returns {Promise<{ key: string, url: string, moderation: { status: string, jobId?: string } }>}
 * @throws {ContentPolicyError}  when Rekognition rejects the file (HTTP 422)
 * @throws {FileTooLargeError}   when the file exceeds the client-side size limit
 */
export async function uploadFile(file, fileType, fileName, entityPath) {
  // Client-side size check — gives immediate feedback before any network call
  const isVideo = VIDEO_MIME_TYPES.includes(file.type)
  const limit = isVideo ? UPLOAD_SIZE_LIMITS.video : UPLOAD_SIZE_LIMITS.image
  if (file.size > limit) {
    const sizeMB = (file.size / 1024 / 1024).toFixed(1)
    const limitMB = (limit / 1024 / 1024).toFixed(0)
    throw new FileTooLargeError(sizeMB, limitMB)
  }

  // Always use presigned S3 URL — file goes browser→S3 directly, bypassing CloudFront WAF
  // which blocks POST bodies > 8KB via the SizeRestrictions_BODY managed rule.
  return uploadViaPresignedUrl(file, fileType, fileName, entityPath)
}

async function uploadViaPresignedUrl(file, fileType, fileName, entityPath) {
  const resolvedName = fileName || (file instanceof File ? file.name : `upload.${mimeToExt(file.type)}`)

  // Step 1: Get presigned S3 PUT URL from Lambda
  const { uploadUrl, key } = await apiImageCall('/upload/init', {
    method: 'POST',
    body: JSON.stringify({ fileType, fileName: resolvedName, mimeType: file.type, entityPath }),
  })

  // Step 2: Upload file directly to S3 (no Lambda in the middle)
  // On Capacitor, CapacitorHttp patches window.fetch and cannot send binary Blob bodies.
  // CapacitorHttp dataType:'file' decodes a base64 string into binary (CapacitorUrlRequest.swift:179).
  // This applies to ALL files on Capacitor — including cropped blobs that have no _base64Data.
  if (isCapacitor) {
    // Use _base64Data if set by the picker; otherwise read the blob as base64 (e.g. after crop)
    const base64Data = file._base64Data || await new Promise((resolve, reject) => {
      const blob = file instanceof File ? new Blob([file], { type: file.type }) : file
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result.split(',')[1])
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    const { CapacitorHttp } = await import('@capacitor/core')
    const res = await CapacitorHttp.request({
      url: uploadUrl,
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      data: base64Data,
      dataType: 'file',
    })
    if (res.status < 200 || res.status >= 300) {
      throw new Error(`S3 upload failed (${res.status})`)
    }
  } else {
    // Web: convert File → Blob so iOS WKWebView fetch() handles it correctly
    const body = file instanceof File
      ? new Blob([await file.arrayBuffer()], { type: file.type })
      : file
    const s3Response = await fetch(uploadUrl, {
      method: 'PUT',
      body,
      headers: { 'Content-Type': file.type },
    })
    if (!s3Response.ok) {
      throw new Error(`S3 upload failed (${s3Response.status})`)
    }
  }

  // Step 3: Trigger Rekognition moderation via Lambda
  try {
    return await apiImageCall('/upload/complete', {
      method: 'POST',
      body: JSON.stringify({ key, fileType, mimeType: file.type }),
    })
  } catch (error) {
    if (error.status === 422) {
      throw new ContentPolicyError(error.message, error.policy)
    }
    throw error
  }
}

/**
 * Move a temp-uploaded S3 object to its canonical entity path.
 * Call this after entity creation when you have the entity ID.
 *
 * @param {string} fromUrl  - Temp URL returned by uploadFile, e.g. '/images/group_assets/uuid.jpeg'
 * @param {string} toKey    - Canonical S3 key (no /images/ prefix), e.g. 'group_assets/{id}/images/background.jpeg'
 * @returns {Promise<{ key: string, url: string }>}
 */
export async function moveImage(fromUrl, toKey) {
  return apiImageCall('/move', {
    body: JSON.stringify({ fromUrl, toKey }),
  })
}

/**
 * Thrown when Rekognition flags a file as policy-violating.
 * UI can catch this specifically to show the policy warning.
 */
export class ContentPolicyError extends Error {
  constructor(message, policy) {
    super(message)
    this.name = 'ContentPolicyError'
    this.policy = policy
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function mimeToExt(mimeType) {
  const map = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'video/mp4': 'mp4',
    'video/quicktime': 'mov',
    'video/webm': 'webm',
  }
  return map[mimeType] || 'bin'
}
